import React from 'react';
import { Mic } from 'lucide-react';

import { useTheme } from '../../contexts/ThemeContext.tsx';

export type ContactPresenceActivity = 'typing' | 'recording' | null;
export type ContactPresenceAvailability = 'online' | 'offline' | null;

export interface ContactPresenceViewState {
  activity: ContactPresenceActivity;
  availability: ContactPresenceAvailability;
  lastSeen: string | null;
}

const isSameLocalDay = (left: Date, right: Date): boolean => (
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()
);

export const formatPresenceLastSeen = (
  value: string | null,
  now: Date = new Date(),
): string | null => {
  if (!value) return null;

  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return null;

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);

  if (isSameLocalDay(parsed, now)) {
    return `visto por último hoje às ${time}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameLocalDay(parsed, yesterday)) {
    return `visto por último ontem às ${time}`;
  }

  const date = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  }).format(parsed);
  return `visto por último em ${date} às ${time}`;
};

export const ContactPresenceStatusLine: React.FC<{
  phone: string;
  availability: ContactPresenceAvailability;
  lastSeen: string | null;
  className?: string;
}> = ({ phone, availability, lastSeen, className = '' }) => {
  const lastSeenLabel = availability === 'online'
    ? null
    : formatPresenceLastSeen(lastSeen);

  if (availability === 'online') {
    return (
      <span
        className={`flex min-w-0 items-center gap-1.5 text-xs text-emerald-600 ${className}`}
        data-testid="contact-presence-status"
      >
        <span className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500" />
        <span className="truncate">online</span>
      </span>
    );
  }

  return (
    <span
      className={`block min-w-0 truncate text-xs text-gray-500 ${className}`}
      data-testid="contact-presence-status"
    >
      {lastSeenLabel || phone}
    </span>
  );
};

export const ContactPresenceTypingDots: React.FC<{
  className?: string;
  dotClassName?: string;
}> = ({
  className = 'h-5 px-1',
  dotClassName = 'h-2 w-2',
}) => (
  <span
    className={`inline-flex items-center gap-1 ${className}`}
    aria-hidden="true"
    data-testid="contact-presence-typing-dots"
  >
    {[0, 1, 2].map((index) => (
      <span
        key={index}
        className={`${dotClassName} rounded-full bg-current animate-bounce motion-reduce:animate-none`}
        data-testid="contact-presence-typing-dot"
        style={{ animationDelay: `${index * 120}ms` }}
      />
    ))}
  </span>
);

const RecordingWave: React.FC = () => (
  <div className="flex h-5 items-center gap-1" aria-hidden="true">
    <Mic className="h-4 w-4 text-sminy-primary" />
    {[10, 16, 12, 18, 11].map((height, index) => (
      <span
        key={`${height}-${index}`}
        className="w-0.5 rounded-full bg-sminy-primary animate-pulse motion-reduce:animate-none"
        style={{
          height,
          animationDelay: `${index * 90}ms`,
        }}
      />
    ))}
  </div>
);

export const ContactPresenceBubble: React.FC<{
  activity: ContactPresenceActivity;
}> = ({ activity }) => {
  const { isDark } = useTheme();
  if (!activity) return null;

  const isTyping = activity === 'typing';
  return (
    <div className="flex justify-start py-2" data-testid="contact-presence-bubble">
      <div
        role="status"
        aria-label={isTyping ? 'Contato está digitando' : 'Contato está gravando áudio'}
        className={`flex h-10 min-w-[56px] items-center justify-center rounded-2xl rounded-bl-md border px-3 shadow-sm transition-colors ${
          isDark
            ? 'border-gray-700 bg-gray-800 text-gray-300'
            : 'border-gray-200 bg-white text-gray-500'
        }`}
        data-testid={isTyping ? 'typing-presence-bubble' : 'recording-presence-bubble'}
      >
        {isTyping ? <ContactPresenceTypingDots /> : <RecordingWave />}
      </div>
    </div>
  );
};
